import React, { useMemo, useState } from 'react';
import '../css/InquiryList.css';
import { domain } from './Domain';
import useFetch from './useFetch';
import Skeleton, { SkeletonTheme } from 'react-loading-skeleton';
import 'react-loading-skeleton/dist/skeleton.css';
import { motion } from 'framer-motion';
import InquiryAnswer from './InquiryAnswer';
import { ToastContainer } from 'react-toastify';

const InquiryList = () => {
    const { data, loading, error, refresh, setRefresh } = useFetch(
        `${domain}/inquiries`
    );

    const [isModalOpen, setIsModalOpen] = useState(false);
    const [selected, setSelected] = useState(null);

    const closeModal = () => setIsModalOpen(false);

    const inquiries = useMemo(() => (Array.isArray(data) ? data : []), [data]);
    const unansweredCount = inquiries.filter((i) => !i.answered).length;
    const cardsArray = Array.from({ length: 4 }, (_, index) => index);

    const handleInquiryClick = (inquiry) => {
        if (inquiry.answered) return;
        setSelected(inquiry);
        setIsModalOpen(true);
    };

    return (
        <section className="inquiry-list" aria-labelledby="inquiry-list-title">
            <ToastContainer />
            <header className="inquiry-list-header">
                <div>
                    <h2 id="inquiry-list-title" className="inquiry-list-title">
                        Inquiry list
                    </h2>
                    {!loading && (
                        <p className="inquiry-list-meta">
                            {inquiries.length} total · {unansweredCount} pending
                            response
                        </p>
                    )}
                </div>
            </header>

            {loading && (
                <div className="inquiry-list-grid" aria-busy="true">
                    <SkeletonTheme baseColor="#e2e8f0" highlightColor="#f8fafc">
                        {cardsArray.map((item) => (
                            <div key={item} className="inquiry-card inquiry-card--loading">
                                <Skeleton width="45%" height={18} />
                                <Skeleton width="70%" height={14} />
                                <Skeleton width="62%" height={14} />
                                <Skeleton width="100%" count={2} />
                                <Skeleton width="40%" height={12} />
                            </div>
                        ))}
                    </SkeletonTheme>
                </div>
            )}

            {!loading && error && (
                <div className="alert alert-danger" role="alert">
                    Failed to load inquiries: {error}
                </div>
            )}

            {!loading && !error && inquiries.length > 0 && (
                <div className="inquiry-list-grid">
                    {inquiries.map((inquiry) => (
                        <motion.article
                            key={inquiry.id}
                            className={`inquiry-card${
                                inquiry.answered ? ' inquiry-card--answered' : ''
                            }`}
                            role={inquiry.answered ? undefined : 'button'}
                            tabIndex={inquiry.answered ? -1 : 0}
                            onClick={() => handleInquiryClick(inquiry)}
                            onKeyDown={(e) => {
                                if (inquiry.answered) return;
                                if (e.key === 'Enter' || e.key === ' ') {
                                    e.preventDefault();
                                    handleInquiryClick(inquiry);
                                }
                            }}
                            whileHover={!inquiry.answered ? { y: -2 } : {}}
                            whileTap={!inquiry.answered ? { scale: 0.99 } : {}}
                        >
                            <div className="inquiry-card-top">
                                <h3 className="inquiry-card-name">{inquiry.name}</h3>
                                <span
                                    className={`inquiry-card-status${
                                        inquiry.answered
                                            ? ' inquiry-card-status--answered'
                                            : ' inquiry-card-status--pending'
                                    }`}
                                >
                                    {inquiry.answered ? 'Answered' : 'Pending'}
                                </span>
                            </div>
                            <p className="inquiry-card-line">
                                <strong>Email:</strong> {inquiry.email}
                            </p>
                            <p className="inquiry-card-line">
                                <strong>Phone:</strong> {inquiry.phone}
                            </p>
                            <p className="inquiry-card-question">{inquiry.question}</p>
                            <small className="inquiry-card-date">
                                Created on {new Date(inquiry.created).toLocaleString()}
                            </small>
                        </motion.article>
                    ))}
                </div>
            )}

            {!loading && !error && inquiries.length === 0 && (
                <div className="inquiry-empty card border-0 shadow-sm">
                    <div className="card-body text-center py-4">
                        <p className="mb-0 text-secondary">There are no inquiries yet.</p>
                    </div>
                </div>
            )}

            <InquiryAnswer
                isOpen={isModalOpen}
                onClose={closeModal}
                selected={selected}
                refresh={refresh}
                setRefresh={setRefresh}
            />
        </section>
    );
};

export default InquiryList;
